import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Lock, ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
  const lastUpdated = 'July 7, 2026';
  const contactEmail = 'arynnlittlejohn@icloud.com';

  return (
    <div className="min-h-screen bg-vault" data-testid="terms-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#9333EA] flex items-center justify-center">
                <Lock className="h-4 w-4 text-white" />
              </div>
              <span className="font-heading text-lg font-bold text-white tracking-tight">FutureVault</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-[#A19DB0] hover:text-white hover:bg-white/5" data-testid="terms-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="font-heading text-4xl font-black text-white tracking-tight mb-2">Terms of Service</h1>
        <p className="text-[#A19DB0] text-sm mb-10">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-[#D4D1E0] leading-relaxed">
          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">1. Acceptance of Terms</h2>
            <p>
              By creating a FutureVault account or using the Service, you agree to these Terms of Service
              (&quot;Terms&quot;) and our Privacy Policy. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">2. What FutureVault Is</h2>
            <p>
              FutureVault is a savings-commitment tool operated by FutureVault LLC, a California Limited Liability Company. It helps you set aside money until a chosen unlock
              date. Funds are held via Stripe Connect in an account linked to your own bank. FutureVault is
              not a bank, broker-dealer, or investment advisor and does not pay interest on held funds.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">3. Eligibility</h2>
            <p>
              You must be at least 18 years old and legally able to enter into a binding contract. You must
              provide accurate information during signup and keep it up to date.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">4. Your Account</h2>
            <p>
              You are responsible for maintaining the confidentiality of your login credentials and for all
              activity that occurs under your account. Notify us immediately at{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>{' '}
              if you suspect unauthorized access.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">5. Deposits, Holding, and Withdrawals</h2>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Deposits are processed by Stripe and sent to your connected Stripe account.</li>
              <li>Funds remain locked until the unlock date you choose at vault creation. Locked vaults are irreversible commitments — you cannot withdraw locked funds early. Limited exceptions (verified death, medical emergency, court order, unauthorized transaction, or account deletion) are described in our <Link to="/refunds" className="text-[#9333EA] underline">Refund Policy</Link>.</li>
              <li>On or after the unlock date, you may initiate a withdrawal (payout) to your linked bank account. Stripe&apos;s standard payout timing applies.</li>
              <li>Commitment and &quot;No Escape&quot; vaults may enforce additional friction or penalties that you agree to at vault creation.</li>
              <li>You are responsible for ensuring your linked bank account is accurate.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">6. Fees</h2>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><span className="text-white font-semibold">Platform fee:</span> 2% of each deposit.</li>
              <li><span className="text-white font-semibold">Payment-processing fees:</span> Stripe&apos;s standard processing fees apply and are passed through to you.</li>
              <li><span className="text-white font-semibold">Premium subscription:</span> $4.99/month for unlimited goals, commitment vaults, and No Escape vaults. Cancellable anytime.</li>
              <li><span className="text-white font-semibold">Auto-renewal:</span> Premium subscriptions renew automatically at the end of each billing period unless you cancel at least 24 hours before the period ends. You can cancel at any time in <span className="text-white">Settings &rsaquo; your name &rsaquo; Subscriptions</span> on iOS. Cancellation takes effect at the end of the current billing period; you retain Premium access until then.</li>
              <li>On iOS and Android, subscriptions are billed by Apple or Google and are governed by their terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">7. Refunds</h2>
            <p>
              Platform fees on completed deposits are non-refundable. Premium subscription refunds follow the
              policies of the platform where the subscription was purchased (Apple App Store for iOS). Full
              details are in our{' '}
              <Link to="/refunds" className="text-[#9333EA] underline">Refund Policy</Link>. For
              account-closure or special-circumstance requests, contact{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">8. Acceptable Use</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Use the Service for any illegal activity, money laundering, or financing of terrorism.</li>
              <li>Attempt to reverse-engineer, disrupt, or probe the Service&apos;s security.</li>
              <li>Create multiple accounts to evade fees, limits, or enforcement actions.</li>
              <li>Use another person&apos;s bank or payment credentials.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">9. Termination</h2>
            <p>
              You may close your account at any time. We may suspend or terminate your account if you violate
              these Terms, engage in fraudulent activity, or if required by law. If your account is
              terminated, you remain entitled to your legally owed funds per Stripe&apos;s and applicable banking
              rules.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">10. Disclaimers</h2>
            <p>
              The Service is provided &quot;as is&quot; without warranties of any kind, express or implied. We do not
              guarantee uninterrupted, error-free, or secure operation. FutureVault is not a substitute for
              professional financial advice.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">11. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, FutureVault and its operators are not liable for any
              indirect, incidental, special, consequential, or punitive damages, or for any loss of profits,
              data, or goodwill arising from your use of the Service. Our total aggregate liability will not
              exceed the greater of (a) $100 USD or (b) the total fees you paid us in the 12 months preceding
              the claim.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">12. Changes to the Service and Terms</h2>
            <p>
              We may update the Service and these Terms at any time. Material changes will be communicated by
              email or in-app notice. Continued use after changes take effect constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">13. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the United States and the State of California, where FutureVault LLC is organized, without regard to conflict-of-laws principles. Any dispute arising from these Terms or use of the Service shall be resolved in the state or federal courts located in Sacramento County, California.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">14. Contact</h2>
            <p>
              Questions about these Terms? Email{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>.
            </p>
          </section>
        </div>

        <div className="mt-12 flex flex-wrap gap-4 text-sm text-[#A19DB0]">
          <Link to="/privacy" className="hover:text-white" data-testid="terms-to-privacy-link">Privacy Policy</Link>
          <span>·</span>
          <Link to="/refunds" className="hover:text-white" data-testid="terms-to-refunds-link">Refund Policy</Link>
          <span>·</span>
          <Link to="/support" className="hover:text-white" data-testid="terms-to-support-link">Support</Link>
        </div>
      </main>
    </div>
  );
}
